import React, { useState, useEffect, useMemo } from 'react';
import { Paper, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Button } from '@mui/material';
import { createChecklistGroup, deleteChecklistGroup, getAllChecklistGroups, getRolesAssignedToChecklist } from 'core/API/checklist';
import { useAssignmentWorkplaceUsers } from 'core/hooks/useAssignmentWorkplaceUsers';
import { createTodoCategory, deleteTodoCategory, getAllTodoCategories, ITodoCategory, updateTodoCategory } from 'core/API/todo';
import styles from './TodoSetting.module.scss';
import checklistStyles from '../CheckListSetting/ChecklistSetting.module.scss';
import { usePermissionCheck } from '../../../../core/hooks/usePermissionCheck';
import { CheckListAndToDoGroups, ChecklistAssignmentGroup } from '../CheckListSetting/CheckListAndToDoGroups';
import { AssignmentGroupForm, AssignmentGroupFormValues, UserOption } from '../CheckListSetting/AssignmentGroupForm';
import { mapChecklistAssignmentGroups } from '../CheckListSetting/mapChecklistAssignmentGroups';
import { useResetSettingsScroll } from '../../utils/resetSettingsScroll';
import TodoCategories from './TodoCategories';
import TodoCategoryForm from './TodoCategoryForm';
import { getCurrentWorkplaceId } from 'core/session/workplaceStore';

const EMPTY_USERS: UserOption[] = [];

// DATA-011: this tab's workplace; no default to workplace 1.
const getWorkplaceId = (): number | null => getCurrentWorkplaceId();

const TodoSetting = () => {
    const { hasViewPermission, hasEditPermission, hasDeletePermission, hasExecutePermission } = usePermissionCheck();
    const canViewTodo = hasViewPermission('To-Do');
    const canEditTodo = hasEditPermission('To-Do');
    const canDeleteTodo = hasDeletePermission('To-Do');
    const canExecuteTodo = hasExecutePermission('To-Do');

    const [currentView, setCurrentView] = useState<
        'TodoSetting' | 'AddAssignmentGroup' | 'EditAssignmentGroup' | 'AddTodoCategory' | 'EditTodoCategory'
    >('TodoSetting');
    useResetSettingsScroll(currentView);
    const [rawAssignmentGroups, setRawAssignmentGroups] = useState<any[]>([]);
    const [editingAssignmentGroup, setEditingAssignmentGroup] = useState<AssignmentGroupFormValues | null>(null);
    const [dynamicRoles, setDynamicRoles] = useState<{ id: number; label: string; backgroundColor: string; textColor: string }[]>([]);
    const { data: users = EMPTY_USERS, isLoading: usersLoading } = useAssignmentWorkplaceUsers();

    const assignmentGroups = useMemo(
        () => mapChecklistAssignmentGroups(rawAssignmentGroups, users),
        [rawAssignmentGroups, users]
    );
    const [loading, setLoading] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [todoCategories, setTodoCategories] = useState<ITodoCategory[]>([]);
    const [editingTodoCategory, setEditingTodoCategory] = useState<ITodoCategory | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState<ChecklistAssignmentGroup | null>(null);
    const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<ITodoCategory | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (!canViewTodo) return;

        const fetchRoles = async () => {
            try {
                const res = await getRolesAssignedToChecklist();
                const sorted = Array.isArray(res) ? [...res].sort((a, b) => a.label.localeCompare(b.label)) : [];
                setDynamicRoles(sorted);
            } catch (error) {
                console.error('Error fetching roles:', error);
                setDynamicRoles([]);
            }
        };

        const fetchGroups = async () => {
            try {
                setLoading(true);
                const apiGroups = await getAllChecklistGroups();
                setRawAssignmentGroups(Array.isArray(apiGroups) ? apiGroups : []);
            } catch (error) {
                console.error('Error fetching todo groups:', error);
                setRawAssignmentGroups([]);
            } finally {
                setLoading(false);
            }
        };

        const fetchTodoCategories = async () => {
            try {
                setCategoriesLoading(true);
                const categories = await getAllTodoCategories();
                setTodoCategories(Array.isArray(categories) ? categories : []);
            } catch (error) {
                console.error('Error fetching to-do categories:', error);
                setTodoCategories([]);
                setSnackbar({
                    open: true,
                    message: 'Failed to fetch to-do categories',
                    severity: 'error',
                });
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchRoles();
        fetchGroups();
        fetchTodoCategories();
    }, [canViewTodo]);

    const refreshGroups = async () => {
        try {
            setLoading(true);
            const apiGroups = await getAllChecklistGroups();
            setRawAssignmentGroups(Array.isArray(apiGroups) ? apiGroups : []);
        } catch (error) {
            console.error('Error fetching todo groups:', error);
            setRawAssignmentGroups([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddAssignmentClick = () => {
        if (!canExecuteTodo) return;
        setEditingAssignmentGroup(null);
        setCurrentView('AddAssignmentGroup');
    };

    const handleEditAssignmentClick = (group: ChecklistAssignmentGroup) => {
        if (!canEditTodo) return;
        setEditingAssignmentGroup({
            id: group.id,
            groupName: group.groupName,
            assignmentType: group.assignmentType ?? 'security_role',
            roleId: group.rolesAssignedId ? String(group.rolesAssignedId) : '',
            selectedUsers: group.selectedUsers ?? [],
        });
        setCurrentView('EditAssignmentGroup');
    };

    const handleSaveAssignmentGroup = async (values: AssignmentGroupFormValues) => {
        const workplaceId = getWorkplaceId();
        const payload: Record<string, any> = {
            workplace: `/api/workplaces/${workplaceId}`,
            label: values.groupName,
            type: values.assignmentType,
            isUpdate: values.id ?? null,
        };

        if (values.assignmentType === 'security_role') {
            payload.securityRole = values.roleId ? `/api/security_roles/${values.roleId}` : null;
            payload.checklistGroupMembers = [];
        } else {
            payload.checklistGroupMembers = values.selectedUsers.map((u) => ({ user: `/api/users/${u.id}` }));
            payload.securityRole = null;
        }

        await createChecklistGroup(payload);

        setSnackbar({
            open: true,
            message: values.id ? 'Assignment group updated successfully' : 'Assignment group created successfully',
            severity: 'success',
        });
        await refreshGroups();
        setCurrentView('TodoSetting');
        setEditingAssignmentGroup(null);
    };

    const handleBackFromAssignmentForm = () => {
        setCurrentView('TodoSetting');
        setEditingAssignmentGroup(null);
    };

    const handleDeleteAssignmentClick = (id: number) => {
        if (!canDeleteTodo) return;
        const group = assignmentGroups.find(g => g.id === id);
        if (group) {
            setGroupToDelete(group);
            setDeleteDialogOpen(true);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!groupToDelete) return;
        try {
            await deleteChecklistGroup(groupToDelete.id);
            await refreshGroups();
            setSnackbar({ open: true, message: 'Assignment group deleted successfully', severity: 'success' });
        } catch (error: any) {
            let errorMessage = 'Failed to delete assignment group';
            if (error && typeof error === 'object') {
                if (error.error) {
                    errorMessage = error.error;
                } else if (error.message) {
                    errorMessage = error.message;
                } else if (error.status === 409) {
                    errorMessage = 'Cannot delete assignment group: It is being used by existing assignments';
                }
            }
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setGroupToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setGroupToDelete(null);
    };

    const refreshTodoCategories = async () => {
        try {
            setCategoriesLoading(true);
            const categories = await getAllTodoCategories();
            setTodoCategories(Array.isArray(categories) ? categories : []);
        } catch (error) {
            console.error('Error refreshing to-do categories:', error);
            setTodoCategories([]);
        } finally {
            setCategoriesLoading(false);
        }
    };

    const handleCreateCategoryClick = () => {
        if (!canExecuteTodo) return;
        setEditingTodoCategory(null);
        setCurrentView('AddTodoCategory');
    };

    const handleAddCategory = async (label: string) => {
        if (!canExecuteTodo) return;

        try {
            await createTodoCategory({ label });
            setSnackbar({ open: true, message: 'Category added successfully', severity: 'success' });
            await refreshTodoCategories();
            setCurrentView('TodoSetting');
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to add category', severity: 'error' });
            throw error;
        }
    };

    const handleEditCategoryClick = (category: ITodoCategory) => {
        if (!canEditTodo) return;
        setEditingTodoCategory(category);
        setCurrentView('EditTodoCategory');
    };

    const handleSaveCategoryEdit = async (label: string) => {
        if (!canEditTodo || !editingTodoCategory) return;

        try {
            await updateTodoCategory(editingTodoCategory.id, { label });
            setSnackbar({ open: true, message: 'Category updated successfully', severity: 'success' });
            await refreshTodoCategories();
            setEditingTodoCategory(null);
            setCurrentView('TodoSetting');
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to update category', severity: 'error' });
            throw error;
        }
    };

    const handleCancelCategoryEdit = () => {
        setEditingTodoCategory(null);
        setCurrentView('TodoSetting');
    };

    const handleDeleteCategoryClick = (category: ITodoCategory) => {
        if (!canDeleteTodo) return;
        setCategoryToDelete(category);
        setDeleteCategoryDialogOpen(true);
    };

    const handleDeleteCategoryConfirm = async () => {
        if (!categoryToDelete) return;
        try {
            await deleteTodoCategory(categoryToDelete.id);
            await refreshTodoCategories();
            setSnackbar({ open: true, message: 'Category deleted successfully', severity: 'success' });
        } catch (error: any) {
            let errorMessage = 'Failed to delete category';
            if (error && typeof error === 'object') {
                if (error.error) {
                    errorMessage = error.error;
                } else if (error.message) {
                    errorMessage = error.message;
                } else if (error.status === 409) {
                    errorMessage = 'Cannot delete category: It is being used by existing todos';
                }
            }
            setSnackbar({ open: true, message: errorMessage, severity: 'error' });
        } finally {
            setDeleteCategoryDialogOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleDeleteCategoryCancel = () => {
        setDeleteCategoryDialogOpen(false);
        setCategoryToDelete(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (!canViewTodo) {
        return (
            <Paper elevation={3} className={styles.containerr}>
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    You do not have permission to view todo settings.
                </div>
            </Paper>
        );
    }

    if (currentView === 'AddAssignmentGroup' || currentView === 'EditAssignmentGroup') {
        return (
            <AssignmentGroupForm
                editValues={currentView === 'EditAssignmentGroup' ? editingAssignmentGroup : null}
                onBack={handleBackFromAssignmentForm}
                onSave={handleSaveAssignmentGroup}
                dynamicRoles={dynamicRoles}
                users={users}
                usersLoading={usersLoading}
                canEdit={canEditTodo}
                canExecute={canExecuteTodo}
            />
        );
    }

    if (currentView === 'AddTodoCategory') {
        return (
            <TodoCategoryForm
                mode="create"
                onBack={handleCancelCategoryEdit}
                onSave={handleAddCategory}
                canSave={canExecuteTodo}
            />
        );
    }

    if (currentView === 'EditTodoCategory' && editingTodoCategory) {
        return (
            <TodoCategoryForm
                mode="edit"
                initialLabel={editingTodoCategory.label}
                onBack={handleCancelCategoryEdit}
                onSave={handleSaveCategoryEdit}
                canSave={canEditTodo}
            />
        );
    }

    return (
        <Paper elevation={3} className={styles.containerr}>
            <div className={styles.contentWrapper}>
                <CheckListAndToDoGroups
                    handleDeleteAssignmentClick={handleDeleteAssignmentClick}
                    assignmentGroups={assignmentGroups}
                    handleEditAssignmentClick={handleEditAssignmentClick}
                    handleAddAssignmentClick={handleAddAssignmentClick}
                    loading={loading}
                    canEdit={canEditTodo}
                    canDelete={canDeleteTodo}
                    canExecute={canExecuteTodo}
                    panelClassName={checklistStyles.naturalHeightPanel}
                />
                <TodoCategories
                    categoriesData={todoCategories}
                    loading={categoriesLoading}
                    canEdit={canEditTodo}
                    canDelete={canDeleteTodo}
                    canExecute={canExecuteTodo}
                    onCreateClick={handleCreateCategoryClick}
                    onEditClick={handleEditCategoryClick}
                    onDeleteCategoryClick={handleDeleteCategoryClick}
                />
            </div>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px',
                    }
                }}
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete the assignment group &ldquo;{groupToDelete?.groupName}&rdquo;? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteCategoryDialogOpen}
                onClose={handleDeleteCategoryCancel}
                aria-labelledby="delete-category-dialog-title"
                aria-describedby="delete-category-dialog-description"
                maxWidth="sm"
                fullWidth
                style={{ zIndex: 9999 }}
                PaperProps={{
                    style: {
                        backgroundColor: 'white',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: '8px',
                        minWidth: '400px',
                    },
                }}
            >
                <DialogTitle id="delete-category-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-category-dialog-description">
                        Are you sure you want to delete the todo category &ldquo;{categoryToDelete?.label}&rdquo;? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDeleteCategoryCancel}
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteCategoryConfirm}
                        style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white', textTransform: 'none', borderRadius: '4px', padding: '5px 15px' }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" className={styles.snackbar}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default TodoSetting;